import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// styles and images
import "../project/Project.css";
import "./VendorInvoices.css";
import Close from "../../assets/close.svg";

// global
import {
  userStore,
  messageAndErrorStore,
  projectStore,
  companyStore,
} from "../../store/Store";

// components
import InputField from "../../formComponents/InputField";
import FormButton from "../../formComponents/FormButton";

export default function GenerateVendorInvoice({ setTriggerInvoice, isAsset }) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const currentProject = projectStore((data) => data.currentProject);
  const projectVendorInvoices = projectStore(
    (data) => data.projectVendorInvoices,
  );
  const projectCustomerInvoices = projectStore(
    (data) => data.projectCustomerInvoices,
  );
  const setProjectVendorInvoices = projectStore(
    (data) => data.setProjectVendorInvoices,
  );
  const setProjectVendorInvoiceTotals = projectStore(
    (data) => data.setProjectVendorInvoiceTotals,
  );
  const setAuthStatus = messageAndErrorStore((data) => data.setAuthStatus);
  const toggleNewMessage = messageAndErrorStore(
    (data) => data.toggleNewMessage,
  );
  const setInvoiceCounts = companyStore((data) => data.setInvoiceCounts);

  const navigate = useNavigate();
  const [vendorDD, setVendorDD] = useState([]);
  const [custInvDD, setCustInvDD] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [pending, setPending] = useState(false);
  const [vendorOptionsPending, setVendorOptionsPending] = useState(true);
  const [vendorInvoice, setVendorInvoice] = useState({
    id: 0,
    project_id: currentProject.project.id,
    invoice_name: "",
    company_id: currentCompany.id,
    vendor_name: "",
    is_sand: false,
    is_asset: isAsset,
    purchase_order: "",
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    customer_invoice_id: 0,
    discount_percent: 0,
    is_insert: true,
    vendor_id: 0,
    pc_carrier_id: "",
  });

  useEffect(() => {
    const dd = [];
    if (projectCustomerInvoices) {
      projectCustomerInvoices.forEach((inv) => {
        var vendorInvoice = [];
        if (projectVendorInvoices.invoices) {
          vendorInvoice = projectVendorInvoices.invoices.filter(
            (x) =>
              x.customer_invoice_id === inv.id &&
              x.vendor_name === vendorInvoice.vendor_name,
          );
        }
        if (vendorInvoice.length === 0) {
          if (inv.is_asset === isAsset) {
            dd.push({ value: inv.id, label: inv.invoice_name });
          }
        }
      });
    }
    setCustInvDD(dd);
  }, [projectCustomerInvoices, isAsset, projectVendorInvoices, vendorInvoice]);

  const { exists } = useVerifyUnique(
    projectVendorInvoices.invoices,
    "invoice_name",
    vendorInvoice.invoice_name,
  );
  // get vendor options for vendors that have loads in customer invoice
  useEffect(() => {
    if (vendorInvoice.customer_invoice_id > 0) {
      const headers = new Headers();
      const controller = new AbortController();

      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/vendor-invoice-options/${vendorInvoice.customer_invoice_id}`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
              return Promise.resolve();
            } else {
              toggleNewMessage(`${res.statusText}`);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            var options = [];
            data.forEach((o) => {
              if (o.account_company_id === 0) {
                options.push({ value: o.pc_carrier_id, label: o.vendor_name });
              } else {
                options.push({
                  value: o.account_company_id,
                  label: o.vendor_name,
                });
              }
            });
            setVendorDD(options);
            setVendorOptionsPending(false);
          }
        })
        .catch((err) => console.log(err));
      return () => controller.abort();
    }
  }, [
    JwtToken,
    navigate,
    setAuthStatus,
    toggleNewMessage,
    vendorInvoice.customer_invoice_id,
  ]);

  const handleClose = () => {
    setTriggerInvoice(false);
  };

  useEscape(setTriggerInvoice, [], [], pending);

  const handleChange = (e) => {
    if (e.target.name === "discount_percent") {
      setVendorInvoice({
        ...vendorInvoice,
        [e.target.name]: parseFloat(e.target.value),
      });
    } else {
      setVendorInvoice({
        ...vendorInvoice,
        [e.target.name]: e.target.value,
      });
    }
  };
  const handleCustomerInv = (o) => {
    setVendorInvoice({
      ...vendorInvoice,
      customer_invoice_id: o.value,
    });
  };
  const handleVendor = (e) => {
    setVendor(e);
    if (typeof e.value === "string") {
      setVendorInvoice({
        ...vendorInvoice,
        pc_carrier_id: e.value,
        vendor_name: e.label,
        vendor_id: 0,
      });
    } else {
      setVendorInvoice({
        ...vendorInvoice,
        vendor_id: e.value,
        vendor_name: e.label,
        pc_carrier_id: "",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    if (exists) {
      toggleNewMessage("That invoice name already exists");
      setPending(false);
      return;
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    var inv = { ...vendorInvoice };
    var cInv = projectCustomerInvoices.filter(
      (x) => x.id === inv.customer_invoice_id,
    );
    if (cInv.length > 0) {
      inv.invoice_name = `${inv.vendor_name} - ${cInv[0].invoice_name}`;
    }
    const reqBody = {
      invoice: inv,
      loads: [],
      project_id: currentProject.project.id,
    };
    const requestOptions = {
      method: "POST",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/vendor-invoice/${vendorInvoice.id}`,
      requestOptions,
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            navigate("/login");
            setAuthStatus(false);
            setPending(false);
            setTriggerInvoice(false);
            return;
          } else if (res.status === 409 && isAsset) {
            toggleNewMessage("No Approved loads");
            return Promise.resolve();
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (!data) {
          return;
        }
        if (data.error) {
          if (data.message === "project is archived and cannot be updated") {
            toggleNewMessage("Project is Archived and cannot be updated");
            setPending(false);
            return;
          } else {
            toggleNewMessage("Something went wrong");
            console.log(data);
            setPending(false);
            return;
          }
        } else {
          toggleNewMessage(null, "Invoice Added");
          setProjectVendorInvoices(data.invoice_data);
          setProjectVendorInvoiceTotals(data.vendor_invoice_totals);
          setInvoiceCounts(data.invoice_counts);
          setPending(false);
          setTriggerInvoice(false);
        }
      })
      .catch((err) => {
        console.log(err);
        toggleNewMessage("Something went wrong");
        setPending(false);
        return;
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };
  return (
    <div className="popup">
      <div className="popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>Generate Vendor Invoice</h3>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <InputField
              name={"discount_percent"}
              type={"number"}
              title={"Discount %"}
              placeholder={"Discount %"}
              onChange={handleChange}
              value={vendorInvoice.discount_percent || ""}
              step="0.01"
            />
          </div>
          <div className="row g-3">
            <span>Customer Invoice</span>
            <Select
              id="cust_inv"
              options={custInvDD}
              onChange={handleCustomerInv}
              placeholder="Select Customer Invoice"
              value={custInvDD.filter(
                (x) => x.value === vendorInvoice.customer_invoice_id,
              )}
              className="select-box"
              required
            />
          </div>
          {!vendorOptionsPending && (
            <div className="row g-3">
              <span>Vendor</span>
              <Select
                id="vendor"
                options={vendorDD}
                onChange={handleVendor}
                placeholder="Select Vendor"
                value={vendor}
                className="select-box"
                required
              />
            </div>
          )}
          <div className="row g-3">
            <div className="col">
              {!vendorOptionsPending && <FormButton name={"Submit"} pending={pending} />}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
